import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Quote, Link2, Unlink, Undo2, Redo2
} from 'lucide-react';

const ToolButton = ({ onClick, active, label, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    data-testid={testId}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors duration-150 ${
      active ? 'bg-teal-600/30 text-teal-300' : 'text-zinc-400 hover:bg-zinc-700 hover:text-white'
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false, autolink: true } })],
    content: value || '',
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: 'rich-content min-h-[260px] px-4 py-3 focus:outline-none', 'data-testid': 'rich-text-editor-content' } },
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? '' : ed.getHTML()),
  });

  useEffect(() => {
    if (editor && (value || '') !== (editor.isEmpty ? '' : editor.getHTML())) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href || 'https://';
    const url = window.prompt('Link URL', prev);
    if (url === null) return;
    if (url === '') return editor.chain().focus().unsetLink().run();
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const c = () => editor.chain().focus();
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-800/60 overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-zinc-700 bg-zinc-900/70 px-2 py-1.5">
        <ToolButton testId="rte-bold" label="Bold" active={editor.isActive('bold')} onClick={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-italic" label="Italic" active={editor.isActive('italic')} onClick={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-underline" label="Underline" active={editor.isActive('underline')} onClick={() => c().toggleUnderline().run()}><Underline className="h-4 w-4" /></ToolButton>
        <span className="w-px h-5 bg-zinc-700 mx-1" />
        <ToolButton testId="rte-h2" label="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-h3" label="Subheading" active={editor.isActive('heading', { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-bullet-list" label="Bullet list" active={editor.isActive('bulletList')} onClick={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-ordered-list" label="Numbered list" active={editor.isActive('orderedList')} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-quote" label="Quote" active={editor.isActive('blockquote')} onClick={() => c().toggleBlockquote().run()}><Quote className="h-4 w-4" /></ToolButton>
        <span className="w-px h-5 bg-zinc-700 mx-1" />
        <ToolButton testId="rte-link" label="Add link" active={editor.isActive('link')} onClick={setLink}><Link2 className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-unlink" label="Remove link" onClick={() => c().unsetLink().run()}><Unlink className="h-4 w-4" /></ToolButton>
        <span className="w-px h-5 bg-zinc-700 mx-1" />
        <ToolButton testId="rte-undo" label="Undo" onClick={() => c().undo().run()}><Undo2 className="h-4 w-4" /></ToolButton>
        <ToolButton testId="rte-redo" label="Redo" onClick={() => c().redo().run()}><Redo2 className="h-4 w-4" /></ToolButton>
      </div>
      <div className="relative text-zinc-100">
        {editor.isEmpty && (
          <p className="pointer-events-none absolute left-4 top-3 text-zinc-500 text-sm">{placeholder}</p>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
